/**
 * VoiceAudioEngine
 * Handles browser microphone capture and audio playback for voice AI sessions.
 *
 * Capture: getUserMedia → AudioContext(16kHz) → ScriptProcessorNode → float32→int16→base64
 * Playback: base64→int16→float32 → AudioBufferSourceNode(24kHz) → sequential queue
 */

const CAPTURE_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;
const BUFFER_SIZE = 2048;

export class VoiceAudioEngine {
  constructor() {
    this.captureContext = null;
    this.playbackContext = null;
    this.mediaStream = null;
    this.scriptProcessor = null;
    this.source = null;
    this.onAudioChunk = null;
    this.isCapturing = false;
    this.isMuted = false;

    // Playback queue
    this.playbackQueue = [];
    this.isPlaying = false;
  }

  /**
   * Start capturing audio from the microphone
   * @param {Function} onChunk - Callback receiving base64-encoded LPCM audio chunks
   */
  async startCapture(onChunk) {
    this.onAudioChunk = onChunk;

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: CAPTURE_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      this.captureContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: CAPTURE_SAMPLE_RATE
      });

      this.source = this.captureContext.createMediaStreamSource(this.mediaStream);

      // ScriptProcessorNode for raw PCM access
      this.scriptProcessor = this.captureContext.createScriptProcessor(BUFFER_SIZE, 1, 1);

      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.isCapturing || this.isMuted) return;

        const float32Data = event.inputBuffer.getChannelData(0);
        const int16Data = float32ToInt16(float32Data);
        const base64 = int16ToBase64(int16Data);

        if (this.onAudioChunk) {
          this.onAudioChunk(base64);
        }
      };

      this.source.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.captureContext.destination);
      this.isCapturing = true;

      console.log('[AudioEngine] Capture started at', this.captureContext.sampleRate, 'Hz');
    } catch (error) {
      console.error('[AudioEngine] Failed to start capture:', error);
      throw error;
    }
  }

  /**
   * Play a base64-encoded audio chunk (24kHz LPCM)
   * @param {string} base64Audio
   */
  playAudioChunk(base64Audio) {
    this.playbackQueue.push(base64Audio);
    if (!this.isPlaying) {
      this._processPlaybackQueue();
    }
  }

  /**
   * Process playback queue sequentially
   */
  async _processPlaybackQueue() {
    if (this.isPlaying || this.playbackQueue.length === 0) return;
    this.isPlaying = true;

    if (!this.playbackContext) {
      this.playbackContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: PLAYBACK_SAMPLE_RATE
      });
    }

    while (this.playbackQueue.length > 0) {
      const base64 = this.playbackQueue.shift();
      try {
        const int16Data = base64ToInt16(base64);
        const float32Data = int16ToFloat32(int16Data);

        const audioBuffer = this.playbackContext.createBuffer(1, float32Data.length, PLAYBACK_SAMPLE_RATE);
        audioBuffer.getChannelData(0).set(float32Data);

        const bufferSource = this.playbackContext.createBufferSource();
        bufferSource.buffer = audioBuffer;
        bufferSource.connect(this.playbackContext.destination);

        await new Promise((resolve) => {
          bufferSource.onended = resolve;
          bufferSource.start();
        });
      } catch (error) {
        console.error('[AudioEngine] Playback error:', error);
      }
    }

    this.isPlaying = false;
  }

  /**
   * Toggle mute
   */
  toggleMute() {
    this.isMuted = !this.isMuted;
    return this.isMuted;
  }

  /**
   * Stop capturing
   */
  stopCapture() {
    this.isCapturing = false;

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    if (this.captureContext && this.captureContext.state !== 'closed') {
      this.captureContext.close();
      this.captureContext = null;
    }

    console.log('[AudioEngine] Capture stopped');
  }

  /**
   * Full cleanup
   */
  destroy() {
    this.stopCapture();
    this.playbackQueue = [];

    if (this.playbackContext && this.playbackContext.state !== 'closed') {
      this.playbackContext.close();
      this.playbackContext = null;
    }
  }
}

// ============================================================
// Audio conversion utilities
// ============================================================

function float32ToInt16(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16;
}

function int16ToFloat32(int16Array) {
  const float32 = new Float32Array(int16Array.length);
  for (let i = 0; i < int16Array.length; i++) {
    float32[i] = int16Array[i] / (int16Array[i] < 0 ? 0x8000 : 0x7FFF);
  }
  return float32;
}

function int16ToBase64(int16Array) {
  const bytes = new Uint8Array(int16Array.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToInt16(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Int16Array(bytes.buffer);
}

export default VoiceAudioEngine;
